/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Redirect, useLocation } from 'react-router';

import { EmptyLayout } from 'src/app/account/championDashboardPage/widgets/EmptyLayout';
import { InsightsExpandButton } from 'src/app/graph/insights/InsightsExpandButton';
import { Loading } from 'src/components/common/loading/Loading';
import { PageTitle } from 'src/components/common/pageTitle/PageTitle';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { InsightsEnforcementWarning } from 'src/components/insightsEnforcementWarning/InsightsEnforcementWarning';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  TimeRangeErrorCode,
  resetTimeRangeLocation,
  useSelectedTimeRange,
} from 'src/hooks/useSelectedTimeRange';
import { TimeRangeOption } from 'src/lib/config/timeRangeOptions';
import { useShowSubgraphInsightsUI } from 'src/lib/featureFlags';

import { ServiceRangeUnavailable } from '../../errors/serviceRangeUnavailable/ServiceRangeUnavailable';
import { AllowedTabsProvider } from '../../operationsPage/allowedTabsProvider/AllowedTabsProvider';
import { SelectedSubgraph } from '../../operationsPage/insightsL3Nav/insightsList/SubgraphsList';
import { useInsightsL3NavState } from '../../operationsPage/insightsL3Nav/useInsightsL3NavState';
import { SetupOperationMetrics } from '../../operationsPage/SetupOperationMetrics';
import { insightsRouteConfig } from '../../routes';

import { SubgraphInsightsFilter } from './SubgraphInsightsFilter';

interface SubgraphMainContentProps {
  serviceId: string | undefined;
  accountId: string | undefined;
  reportingEnabled?: boolean;
  selectedSubgraph?: SelectedSubgraph;
}

export const SubgraphInsightsMainContent = ({
  serviceId,
  accountId,
  reportingEnabled,
  selectedSubgraph,
}: SubgraphMainContentProps) => {
  const location = useLocation();
  const { isOpen, onOpen } = useInsightsL3NavState();

  const { insightsTab, fastModeRange } = useRouteParams(insightsRouteConfig);
  const showSubgraphInsights = useShowSubgraphInsightsUI();
  const {
    timeRangeInfo,
    loading: timeRangeLoading,
    error: timeRangeError,
  } = useSelectedTimeRange();
  if (!serviceId) {
    return null;
  }

  if (insightsTab !== 'subgraphs' || !showSubgraphInsights) {
    return (
      <Redirect
        to={insightsRouteConfig.locationFrom({
          location,
          patch: { insightsTab: 'operations' },
        })}
      />
    );
  }

  if (timeRangeError?.code === TimeRangeErrorCode.INVALID) {
    return <Redirect to={resetTimeRangeLocation(location)} />;
  }

  const title = selectedSubgraph?.name ?? 'Subgraphs';

  return (
    <AllowedTabsProvider isDemoPage={!accountId}>
      {() => {
        return (
          <div className="flex flex-1 flex-col bg-primary">
            <InsightsEnforcementWarning />
            <div className="sticky top-0 z-20 border-b border-primary bg-primary px-8 pt-4">
              <PageTitle
                className="border-b-0 px-[0px]"
                title={title}
                leftAdornment={
                  !isOpen ? (
                    <Tooltip label="Expand">
                      <InsightsExpandButton onOpen={onOpen} />
                    </Tooltip>
                  ) : null
                }
                rightSide={
                  <div className="flex items-center">
                    {reportingEnabled && timeRangeInfo && (
                      <SubgraphInsightsFilter
                        ranges={
                          timeRangeInfo.accountCapabilities
                            .ranges as ReadonlyArray<TimeRangeOption>
                        }
                        maxRangeInDays={
                          timeRangeInfo.accountCapabilities.maxRangeInDays
                        }
                        fastModeRange={fastModeRange}
                      />
                    )}
                  </div>
                }
              />
            </div>
            <div className="relative size-full px-8 pt-6">
              <div className="flex justify-center">
                {!reportingEnabled ? (
                  <SetupOperationMetrics />
                ) : timeRangeLoading ? (
                  <Loading />
                ) : !timeRangeInfo ? (
                  <ServiceRangeUnavailable />
                ) : (
                  <EmptyLayout
                    title="Subgraph Insights are not yet supported"
                    body="Subgraphs are not yet supported in Insights. Please check back soon."
                  />
                )}
              </div>
            </div>
          </div>
        );
      }}
    </AllowedTabsProvider>
  );
};
